/**
 * Responses to an assignment (migration 1028; Harry Max's Requests & Agreements card).
 *
 * A request is answered four ways, and Accept is the first (the Promise). This file draws
 * the rest and what follows from them: Decline, Counteroffer and a date to get back by on
 * a request waiting; Renegotiate and Report done on a promise in force; what is in play
 * (signals, items, commitments, decisions, desired outcome briefs); and the requester's
 * answer. Escalate (1036, the Delegation module): a boundary was hit, on a promise in
 * force; Report done and its answer take the request's level (Approve at 3, Release at 4).
 * All of it belongs to the Requests & Agreements module (1029). Who may do
 * what is decided by availableResponses and mayAnswerResponse in assignmentService.ts, and
 * the server stays the authority.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Modal, TextArea, TextInput, Field, Select } from '@/components/ds';
import { formatDecisionDate } from '@/components/prioritization/decisions/decisionRecordText';
import { recordSignalEvent } from '@/lib/signalEventsService';
import {
  answerAssignmentResponse,
  listGroundCandidates,
  raisedAheadWords,
  respondToAssignment,
  responseOutcomeWord,
  withdrawAssignmentResponse,
  GROUND_KIND_WORDS,
  RESPONSE_WORDS,
  type AssignmentGround,
  type AssignmentResponseAnswer,
  type AssignmentResponseKind,
  type AssignmentResponseRow,
  type AssignmentView,
  type OfferedTerms,
} from '@/lib/assignmentService';
import {
  LADDER_LEVELS,
  STANDARD_TRIGGERS,
  confirmDoneWords,
  delegationLevelLabel,
  levelCap,
  levelPrompts,
  reportDoneWords,
  requiredLines,
  type Delegation,
  type DelegationLadder,
} from '@/lib/delegation';

const when = (d: string | null | undefined) => (d ? formatDecisionDate(d) : null);

const PRIMARY = 'text-xs px-3 py-1.5 bg-[var(--text-pri)] text-[var(--surface)] hover:opacity-90 disabled:opacity-40';
const QUIET = 'text-xs px-3 py-1.5 text-[var(--text-sec)] hover:text-[var(--text-pri)]';

/** One term a person offered, as a line. */
function offeredTermLines(t: OfferedTerms | null | undefined, unitNow?: string | null, ladder: DelegationLadder = 'seven'): string[] {
  if (!t) return [];
  const lines: string[] = [];
  if (t.delegation_level) lines.push(`Delegated at ${delegationLevelLabel(t.delegation_level, ladder)}`);
  if (t.due_on) lines.push(`By ${when(t.due_on)}`);
  if (t.quantity != null) lines.push(`Quantity ${t.quantity}${t.unit ?? unitNow ? ` ${t.unit ?? unitNow}` : ''}`);
  else if (t.unit) lines.push(`Unit: ${t.unit}`);
  if (t.scope_statement) lines.push(`Scope: ${t.scope_statement}`);
  if (t.acceptance_standard) lines.push(`Met when: ${t.acceptance_standard}`);
  if (t.success_standard) lines.push(`Success would be: ${t.success_standard}`);
  if (t.delegation_may) lines.push(`May: ${t.delegation_may}`);
  if (t.delegation_may_not) lines.push(`Not: ${t.delegation_may_not}`);
  if (t.delegation_come_back_if) lines.push(`Come back if: ${t.delegation_come_back_if}`);
  if (t.delegation_report_back) lines.push(`Report back: ${t.delegation_report_back}`);
  return lines;
}

function Grounds({ grounds }: { grounds: unknown }) {
  const list = Array.isArray(grounds) ? (grounds as AssignmentGround[]) : [];
  if (list.length === 0) return null;
  return (
    <div className="mt-1 text-xs text-[var(--text-sec)]">
      <span className="text-[var(--text-ter)]">In play:</span>{' '}
      {list.map((g, i) => (
        <span key={`${g.kind}:${g.id}`}>
          {i > 0 ? '; ' : ''}
          <span className="text-[var(--text-ter)]">{GROUND_KIND_WORDS[g.kind]}:</span> {g.label ?? 'not visible to you'}
        </span>
      ))}
    </div>
  );
}

/** What is being asked, in full: the card's "Do you have all of the information you need?" */
export function RequestInFull({ row, requesterName, showDue = true }: { row: AssignmentView; requesterName?: string | null; showDue?: boolean }) {
  const pending = !!row.proposed_revision_id;
  const scope = pending ? (row.proposed_scope_statement ?? row.scope_statement) : row.scope_statement;
  const sufficient = pending ? (row.proposed_acceptance_standard ?? row.acceptance_standard) : row.acceptance_standard;
  const success = pending ? (row.proposed_success_standard ?? row.success_standard) : row.success_standard;
  const due = pending ? (row.proposed_due_on ?? row.due_on) : row.due_on;
  const context = pending ? row.proposed_basis : null;
  const lines: Array<[string, ReactNode]> = [];
  if (requesterName) lines.push(['Asked by', requesterName]);
  if (due && showDue) lines.push(['By', when(due)]);
  if (scope) lines.push(['Scope', scope]);
  if (sufficient) lines.push(['Met when', sufficient]);
  if (success) lines.push(['Success would be', success]);
  if (context) lines.push(['What got us here', context]);
  if (pending && row.respond_by) lines.push(['Answer needed by', when(row.respond_by)]);
  if (pending && row.if_no_response) lines.push(['If no answer comes', row.if_no_response]);
  // A request that leaves a part of the card out says so: it is what to ask about before answering.
  const missing = pending ? [!sufficient && 'how it will be judged done', !due && 'by when'].filter(Boolean) : [];
  return (
    <div className="space-y-0.5">
      {lines.map(([k, v]) => (
        <div key={k} className="text-xs text-[var(--text-sec)]"><span className="text-[var(--text-ter)]">{k}:</span> {v}</div>
      ))}
      {missing.length > 0 && (
        <div className="text-xs text-[var(--mustard)]">This request doesn’t say {missing.join(' or ')}.</div>
      )}
      {pending && <Grounds grounds={row.proposed_grounds} />}
    </div>
  );
}

/** What was said, on the assignment's row: open, or the last answer. */
export function ResponseReadout({
  response, whoSaid, iSaidIt, mayAnswer, mayAskAgain, onAnswer, onAskAgain, onWithdraw, unitNow, ladder = 'seven',
}: {
  response: AssignmentResponseRow;
  whoSaid: string;
  iSaidIt: boolean;
  mayAnswer: boolean;
  mayAskAgain: boolean;
  onAnswer: (answer: AssignmentResponseAnswer) => void;
  onAskAgain: () => void;
  onWithdraw: () => void;
  unitNow?: string | null;
  ladder?: DelegationLadder;
}) {
  const open = !response.outcome;
  const terms = response.terms as OfferedTerms | null;
  const lines = offeredTermLines(terms, unitNow, ladder);
  const done = confirmDoneWords(response.delegation_level_when_said);
  const escalated = response.response === 'escalate';
  const lead = response.response === 'decline' ? `${whoSaid} declined`
    : response.response === 'counteroffer' ? `${whoSaid} made a counteroffer`
    : response.response === 'renegotiate' ? `${whoSaid} ${iSaidIt ? 'said you' : 'said they'} won’t be able to do this as promised`
    : response.response === 'report_done' ? `${whoSaid} ${iSaidIt ? 'reported this done' : 'says this is done'}`
    : escalated ? `${whoSaid} ${iSaidIt ? 'escalated: a boundary was hit' : open ? 'hit a boundary and needs your call' : 'hit a boundary'}`
    : `${whoSaid} will answer by ${when(response.get_back_by)}`;
  const raised = raisedAheadWords(response.days_before_due);
  return (
    <div className={`mt-2 border-t border-[var(--rule)] pt-2 ${open ? '' : 'opacity-80'}`} data-testid={`assignment-response-${response.response}`}>
      <div className="text-xs text-[var(--text-pri)]">
        <span className="font-medium">{lead}</span>
        {when(response.said_at) && <span className="text-[var(--text-ter)]"> · {when(response.said_at)}</span>}
        {!open && <span className="text-[var(--text-ter)]"> · {responseOutcomeWord(response.outcome, response.response, response.delegation_level_when_said)}</span>}
      </div>
      <div className="mt-0.5 text-xs text-[var(--text-sec)]">“{response.basis}”</div>
      {response.escalation_trigger && (
        <div className="mt-0.5 text-xs text-[var(--text-sec)]"><span className="text-[var(--text-ter)]">Boundary:</span> {response.escalation_trigger}</div>
      )}
      {response.report_link && (
        <a href={response.report_link} target="_blank" rel="noreferrer" className="mt-0.5 block text-xs text-[var(--text-sec)] underline underline-offset-4 hover:text-[var(--text-pri)] break-all">
          Open the work
        </a>
      )}
      {raised && open && <div className="mt-0.5 text-xs text-[var(--text-ter)]">{raised}.</div>}
      {response.help_offered && (
        <div className="mt-0.5 text-xs text-[var(--text-sec)]"><span className="text-[var(--text-ter)]">Can help by:</span> {response.help_offered}</div>
      )}
      {lines.length > 0 && (
        <div className="mt-0.5 text-xs text-[var(--text-sec)]">
          <span className="text-[var(--text-ter)]">Offers:</span> {lines.join(' · ')}
        </div>
      )}
      <Grounds grounds={response.grounds} />
      {response.get_back_overdue && <div className="mt-0.5 text-xs text-[var(--coral)]">That date has passed.</div>}
      {open && (response.response === 'renegotiate' || escalated) && (
        <div className="mt-0.5 text-xs text-[var(--mustard)]">The promise stays in force until this is answered.</div>
      )}
      {open && response.response === 'report_done' && !mayAnswer && (
        <div className="mt-0.5 text-xs text-[var(--text-ter)]">Waiting for the person who asked to confirm it.</div>
      )}
      {!open && response.closing_basis && (
        <div className="mt-0.5 text-xs text-[var(--text-sec)]"><span className="text-[var(--text-ter)]">Answer:</span> {response.closing_basis}</div>
      )}
      {open && (mayAnswer || mayAskAgain || iSaidIt) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          {mayAnswer && response.response === 'report_done' && (
            <button type="button" onClick={() => onAnswer('accept')} className="min-h-[44px] md:min-h-0 text-xs px-3 py-1 bg-[var(--text-pri)] text-[var(--surface)] hover:opacity-90">
              {done.accept}
            </button>
          )}
          {mayAnswer && escalated && (
            <button type="button" onClick={() => onAnswer('accept')} className="min-h-[44px] md:min-h-0 text-xs px-3 py-1 bg-[var(--text-pri)] text-[var(--surface)] hover:opacity-90">
              Go ahead
            </button>
          )}
          {mayAnswer && terms && (
            <button type="button" onClick={() => onAnswer('accept')} className="min-h-[44px] md:min-h-0 text-xs px-3 py-1 bg-[var(--text-pri)] text-[var(--surface)] hover:opacity-90">
              Accept the new terms
            </button>
          )}
          {mayAnswer && response.response === 'decline' && (
            <button type="button" onClick={() => onAnswer('accept')} className="min-h-[44px] md:min-h-0 text-xs text-[var(--text-sec)] underline underline-offset-4 hover:text-[var(--text-pri)]">
              Accept the no
            </button>
          )}
          {mayAnswer && response.response !== 'decline' && (
            <button type="button" onClick={() => onAnswer('decline')} className="min-h-[44px] md:min-h-0 text-xs text-[var(--text-sec)] underline underline-offset-4 hover:text-[var(--text-pri)]">
              {response.response === 'report_done' ? done.decline
                : escalated ? 'Stay within the terms'
                : `Decline${response.response === 'renegotiate' ? ', hold to the promise' : ''}`}
            </button>
          )}
          {mayAskAgain && response.response !== 'report_done' && (
            <button type="button" onClick={onAskAgain} className="min-h-[44px] md:min-h-0 text-xs text-[var(--text-sec)] underline underline-offset-4 hover:text-[var(--text-pri)]">
              {response.response === 'renegotiate' ? 'Amend or transfer' : escalated ? 'Change the terms' : 'Ask again'}
            </button>
          )}
          {iSaidIt && response.response !== 'decline' && (
            <button type="button" onClick={onWithdraw} className="min-h-[44px] md:min-h-0 text-xs text-[var(--text-ter)] underline underline-offset-4 hover:text-[var(--coral)]">Take this back</button>
          )}
        </div>
      )}
    </div>
  );
}

/** What is in play: pick what is on the record, or record what changed as a signal. */
export function GroundsPicker({
  workspaceId, value, onChange, extra = [], signalItemId,
}: {
  workspaceId: string | null;
  value: AssignmentGround[];
  onChange: (next: AssignmentGround[]) => void;
  /** Candidates the page already holds: linked items, the commitment's other assignments. */
  extra?: AssignmentGround[];
  /** The item a signal recorded here is about. */
  signalItemId?: string | null;
}) {
  const [loaded, setLoaded] = useState<AssignmentGround[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [signal, setSignal] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    if (!workspaceId) return undefined;
    listGroundCandidates(workspaceId, signalItemId)
      .then((rows) => { if (live) setLoaded(rows); })
      .catch((e: unknown) => { if (live) setError(e instanceof Error ? e.message : String(e)); });
    return () => { live = false; };
  }, [workspaceId, signalItemId]);

  const key = (g: AssignmentGround) => `${g.kind}:${g.id}`;
  const chosen = useMemo(() => new Set(value.map(key)), [value]);
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const seen = new Set<string>();
    return [...loaded, ...extra].filter((g) => {
      if (seen.has(key(g)) || chosen.has(key(g))) return false;
      seen.add(key(g));
      return !q || `${g.label ?? ''} ${GROUND_KIND_WORDS[g.kind]}`.toLowerCase().includes(q);
    }).slice(0, q ? 20 : 6);
  }, [loaded, extra, chosen, query]);

  async function recordIt() {
    const message = signal.trim();
    if (!workspaceId || !message) return;
    setSaving(true); setError(null);
    const res = await recordSignalEvent({
      workspaceId, occurrence: 'Something changed', message, itemId: signalItemId ?? undefined, status: 'monitoring',
    });
    setSaving(false);
    const id = res.success ? res.signalEventId : undefined;
    if (!id) { setError(res.error ?? 'The signal could not be recorded.'); return; }
    onChange([...value, { kind: 'signal', id, label: message }]);
    setSignal('');
  }

  return (
    <Field label="What is in play" hint="What on the record bears on this. It stays with the response and on any decision that follows.">
      {value.length > 0 && (
        <ul className="mb-2 space-y-1">
          {value.map((g) => (
            <li key={key(g)} className="flex items-start justify-between gap-2 text-xs text-[var(--text-sec)] border border-[var(--rule)] px-2 py-1">
              <span className="min-w-0"><span className="text-[var(--text-ter)]">{GROUND_KIND_WORDS[g.kind]}:</span> {g.label}</span>
              <button type="button" aria-label={`Remove ${g.label ?? 'this'}`} onClick={() => onChange(value.filter((v) => key(v) !== key(g)))}
                className="flex-shrink-0 text-[var(--text-ter)] hover:text-[var(--coral)]">Remove</button>
            </li>
          ))}
        </ul>
      )}
      <TextInput value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search signals, items, commitments, decisions, briefs" />
      {matches.length > 0 && (
        <ul className="mt-1 border border-[var(--rule)] divide-y divide-[var(--rule)]">
          {matches.map((g) => (
            <li key={key(g)}>
              <button type="button" onClick={() => onChange([...value, g])}
                className="w-full min-h-[44px] md:min-h-0 text-left px-2 py-1.5 text-xs text-[var(--text-sec)] hover:bg-[var(--surface)]">
                <span className="text-[var(--text-ter)]">{GROUND_KIND_WORDS[g.kind]}:</span> {g.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 flex flex-col sm:flex-row sm:items-start gap-2">
        <TextInput className="flex-1" value={signal} onChange={(e) => setSignal(e.target.value)} placeholder="Not on the record? Say what changed" />
        <button type="button" disabled={!signal.trim() || saving} onClick={() => void recordIt()}
          className="flex-shrink-0 min-h-[44px] md:min-h-0 text-xs px-2 py-1.5 border border-[var(--rule)] text-[var(--text-sec)] hover:text-[var(--text-pri)] disabled:opacity-40">
          {saving ? 'Recording…' : 'Record as a signal'}
        </button>
      </div>
      {error && <div className="text-xs text-[var(--coral)] mt-1">{error}</div>}
    </Field>
  );
}

/** Terms a person offers. Blank keeps what was asked. */
function TermsFields({ row, terms, onChange, delegation, ladder }: {
  row: AssignmentView;
  terms: TermsDraft;
  onChange: (t: TermsDraft) => void;
  /** Delegation on (1036): the level of the revision answered, so a different one can be offered. */
  delegation?: Delegation | null;
  ladder?: DelegationLadder;
}) {
  const set = (k: keyof TermsDraft) => (e: { target: { value: string } }) => onChange({ ...terms, [k]: e.target.value });
  const due = row.proposed_due_on ?? row.due_on;
  const level = terms.level ? Number(terms.level) : null;
  const need = requiredLines(level);
  const above = !!delegation && !!level && level > levelCap(delegation.reversible, delegation.risk, ladder);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="By" hint={due ? `Asked: ${when(due)}` : undefined}><TextInput type="date" value={terms.dueOn} onChange={set('dueOn')} /></Field>
        <Field label="Quantity"><TextInput type="number" value={terms.quantity} onChange={set('quantity')} /></Field>
        <Field label="Unit"><TextInput value={terms.unit} onChange={set('unit')} placeholder={row.proposed_unit ?? row.unit ?? ''} /></Field>
      </div>
      <Field label="Scope"><TextInput value={terms.scope} onChange={set('scope')} placeholder={row.proposed_scope_statement ?? row.scope_statement ?? 'What is in, what is out'} /></Field>
      <Field label="Met when"><TextArea rows={2} value={terms.sufficient} onChange={set('sufficient')} placeholder={row.proposed_acceptance_standard ?? row.acceptance_standard ?? ''} /></Field>
      <Field label="Success would be"><TextArea rows={2} value={terms.success} onChange={set('success')} placeholder={row.proposed_success_standard ?? row.success_standard ?? ''} /></Field>
      {delegation && ladder && (
        <>
          <Field label="Level" hint={`Asked: ${delegationLevelLabel(delegation.level, ladder)}`}>
            <Select value={terms.level} onChange={(e) => {
              // A new level brings its own report line, the model's words, to edit (never the old level's).
              const picked = e.target.value;
              onChange({ ...terms, level: picked, reportBack: picked ? levelPrompts(Number(picked)).reportBack : '' });
            }} placeholder="Keep the level asked"
              options={LADDER_LEVELS[ladder].filter((l) => l !== delegation.level).map((l) => ({ value: String(l), label: delegationLevelLabel(l, ladder) }))} />
          </Field>
          {above && (
            <Field label="Why go above what the model recommends?" required><TextInput value={terms.aboveReason} onChange={set('aboveReason')} /></Field>
          )}
          {need.may && !delegation.may && (
            <Field label="You may…" required><TextInput value={terms.may} onChange={set('may')} /></Field>
          )}
          {need.mayNot && !delegation.mayNot && (
            <Field label="Do not…" required><TextInput value={terms.mayNot} onChange={set('mayNot')} /></Field>
          )}
          {need.comeBackIf && !delegation.comeBackIf && (
            <Field label="Come back if…" required><TextInput value={terms.comeBackIf} onChange={set('comeBackIf')} /></Field>
          )}
          {level !== null && (
            <Field label="Report back…" hint={delegation.reportBack ? `Asked: ${delegation.reportBack}` : undefined}>
              <TextInput value={terms.reportBack} onChange={set('reportBack')} />
            </Field>
          )}
        </>
      )}
    </div>
  );
}

interface TermsDraft {
  dueOn: string; quantity: string; unit: string; scope: string; sufficient: string; success: string;
  level: string; aboveReason: string; may: string; mayNot: string; comeBackIf: string; reportBack: string;
}
const NO_TERMS: TermsDraft = {
  dueOn: '', quantity: '', unit: '', scope: '', sufficient: '', success: '',
  level: '', aboveReason: '', may: '', mayNot: '', comeBackIf: '', reportBack: '',
};

function termsOf(d: TermsDraft): OfferedTerms | null {
  const t: OfferedTerms = {};
  if (d.dueOn) t.due_on = d.dueOn;
  if (d.quantity) t.quantity = Number(d.quantity);
  if (d.unit.trim()) t.unit = d.unit.trim();
  if (d.scope.trim()) t.scope_statement = d.scope.trim();
  if (d.sufficient.trim()) t.acceptance_standard = d.sufficient.trim();
  if (d.success.trim()) t.success_standard = d.success.trim();
  if (d.level) {
    t.delegation_level = Number(d.level);
    if (d.aboveReason.trim()) t.delegation_above_reason = d.aboveReason.trim();
    if (d.may.trim()) t.delegation_may = d.may.trim();
    if (d.mayNot.trim()) t.delegation_may_not = d.mayNot.trim();
    if (d.comeBackIf.trim()) t.delegation_come_back_if = d.comeBackIf.trim();
    if (d.reportBack.trim()) t.delegation_report_back = d.reportBack.trim();
  }
  return Object.keys(t).length ? t : null;
}

export function RespondModal({
  row, kind, subject, requesterName, workspaceId, extraGrounds, delegation = null, delegationOn = false, ladder = 'seven', onClose, onDone,
}: {
  row: AssignmentView;
  kind: AssignmentResponseKind;
  /** How much is delegated on the revision answered (1036). */
  delegation?: Delegation | null;
  delegationOn?: boolean;
  ladder?: DelegationLadder;
  /** "deliver Single sign-on". */
  subject: string;
  requesterName?: string | null;
  workspaceId: string | null;
  extraGrounds: AssignmentGround[];
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const words = RESPONSE_WORDS[kind];
  const [basis, setBasis] = useState('');
  const [help, setHelp] = useState('');
  const [getBackBy, setGetBackBy] = useState('');
  const [terms, setTerms] = useState<TermsDraft>(NO_TERMS);
  const [offering, setOffering] = useState(kind === 'counteroffer');
  const [grounds, setGrounds] = useState<AssignmentGround[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [trigger, setTrigger] = useState('');
  const [otherTrigger, setOtherTrigger] = useState('');
  const [link, setLink] = useState('');

  const offered = termsOf(terms);
  const renegotiate = kind === 'renegotiate';
  const escalate = kind === 'escalate';
  const reportWords = kind === 'report_done' ? reportDoneWords(delegation?.level) : null;
  // The request's own "Come back if" first, then the model's triggers.
  const triggers = [...(delegation?.comeBackIf ? [delegation.comeBackIf] : []), ...STANDARD_TRIGGERS];
  const triggerSaid = trigger === 'other' ? otherTrigger.trim() : trigger;
  // Renegotiating, reporting done and escalating answer the promise in force; the others answer a request waiting.
  const onThePromise = renegotiate || kind === 'report_done' || escalate;
  const ready = !!basis.trim()
    && (!escalate || !!triggerSaid)
    && (kind !== 'commit_to_commit' || !!getBackBy)
    && (kind !== 'counteroffer' || !!offered)
    && (!renegotiate || !!help.trim() || !!offered);

  async function submit() {
    const revisionId = onThePromise ? row.in_force_revision_id : row.proposed_revision_id;
    if (!revisionId) return;
    setSaving(true); setErr(null);
    const res = await respondToAssignment(revisionId, kind, {
      basis: basis.trim(),
      helpOffered: help.trim() || null,
      getBackBy: getBackBy || null,
      terms: offering ? offered : null,
      grounds,
      escalationTrigger: escalate ? triggerSaid : null,
      reportLink: reportWords?.link ? (link.trim() || null) : null,
    });
    setSaving(false);
    if (!res.ok) { setErr(res.error); return; }
    await onDone();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={words.title}
      description={words.meaning}
      size="lg"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className={QUIET}>Cancel</button>
          <button type="button" disabled={!ready || saving} onClick={() => void submit()} className={PRIMARY}>
            {saving ? 'Recording…' : words.act}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="border border-[var(--rule)] bg-[var(--surface)] px-3 py-2">
          <div className="text-sm text-[var(--text-pri)] mb-1">{onThePromise ? 'You said you would ' : 'You are asked to '}{subject}</div>
          <RequestInFull row={row} requesterName={requesterName} />
        </div>

        {escalate && (
          <Field label="Which boundary was hit?" required>
            <div role="radiogroup" aria-label="Which boundary was hit?" className="space-y-1">
              {[...triggers, 'other'].map((t) => (
                <label key={t} className="flex min-h-[44px] md:min-h-0 items-start gap-2 text-xs text-[var(--text-sec)]">
                  <input type="radio" name="escalation-trigger" checked={trigger === t} onChange={() => setTrigger(t)} className="mt-0.5" />
                  <span>{t === 'other' ? 'Something else' : t}</span>
                </label>
              ))}
            </div>
            {trigger === 'other' && (
              <TextInput className="mt-2" value={otherTrigger} onChange={(e) => setOtherTrigger(e.target.value)} placeholder="The boundary, in a few words" />
            )}
          </Field>
        )}

        <Field label={reportWords?.basisLabel ?? words.basisLabel} required>
          <TextArea rows={2} value={basis} onChange={(e) => setBasis(e.target.value)} placeholder={reportWords?.placeholder} />
        </Field>

        {reportWords?.link && (
          <Field label={reportWords.linkLabel} hint="Optional. The person who asked opens it from the request.">
            <TextInput type="url" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://" />
          </Field>
        )}

        {kind === 'commit_to_commit' && (
          <Field label="You will answer by" required hint="The person who asked is told if this date passes.">
            <TextInput type="date" value={getBackBy} onChange={(e) => setGetBackBy(e.target.value)} />
          </Field>
        )}

        {renegotiate && (
          <Field label="What can you do to help find another way?" required={!offered} hint="New terms below can stand in for this.">
            <TextArea rows={2} value={help} onChange={(e) => setHelp(e.target.value)} />
          </Field>
        )}

        {renegotiate && !offering && (
          <button type="button" onClick={() => setOffering(true)} className="text-xs text-[var(--text-sec)] hover:text-[var(--text-pri)] underline underline-offset-4">
            Offer different terms
          </button>
        )}
        {offering && <TermsFields row={row} terms={terms} onChange={setTerms} delegation={delegationOn ? delegation : null} ladder={ladder} />}

        {kind !== 'commit_to_commit' && kind !== 'report_done' && (
          <GroundsPicker workspaceId={workspaceId} value={grounds} onChange={setGrounds} extra={extraGrounds} signalItemId={row.item_id} />
        )}
        {err && <div className="text-xs text-[var(--coral)]">{err}</div>}
      </div>
    </Modal>
  );
}

export function AnswerModal({
  response, answer, onClose, onDone, unitNow, ladder = 'seven',
}: {
  response: AssignmentResponseRow;
  answer: AssignmentResponseAnswer;
  onClose: () => void;
  onDone: () => void | Promise<void>;
  unitNow?: string | null;
  ladder?: DelegationLadder;
}) {
  const [basis, setBasis] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const accepting = answer === 'accept';
  const isNo = response.response === 'decline';
  const isDone = response.response === 'report_done';
  const escalated = response.response === 'escalate';
  const level = response.delegation_level_when_said;
  const done = confirmDoneWords(level);
  const lines = offeredTermLines(response.terms as OfferedTerms | null, unitNow, ladder);
  const title = isDone ? (accepting ? done.accept : done.decline)
    : escalated ? (accepting ? 'Go ahead' : 'Stay within the terms')
    : isNo ? 'Accept the no'
    : accepting ? 'Accept the new terms'
    : response.response === 'renegotiate' ? 'Hold to the promise' : 'Decline the counteroffer';
  const description = isDone
    ? (accepting
      ? (level === 3 ? 'You approve the recommendation. The assignment is fulfilled, and you are recorded as who approved it. Acting on it is a new request. This writes a decision.'
        : level === 4 ? 'You release the draft. The assignment is fulfilled, and you are recorded as who released it. Sending or publishing it stays with you unless they were given that. This writes a decision.'
        : 'It was delivered as asked. The assignment is fulfilled, and you are recorded as who verified it. This writes a decision.')
      : 'The promise stands, and they are told what is missing. This writes a decision.')
    : escalated
    ? (accepting ? 'They carry on past this boundary, this once. The terms stay as they are. This writes a decision.'
      : 'The boundary holds, and they are told why. To change what they may do, change the terms instead. This writes a decision.')
    : isNo
    ? (response.result_revision_id ? '' : 'The request stands declined. If nothing else is in force the assignment is released. The record keeps why.')
    : accepting ? 'These terms bind at once: both sides have said yes. This writes a decision.'
    : response.response === 'renegotiate' ? 'The promise stays in force as it is, and they are told why. This writes a decision.'
    : 'The request stands as you asked it, and the answer is theirs again. This writes a decision.';
  async function submit() {
    if (!response.response_id) return;
    setSaving(true); setErr(null);
    const res = await answerAssignmentResponse(response.response_id, answer, basis.trim() || null);
    setSaving(false);
    if (!res.ok) { setErr(res.error); return; }
    await onDone();
  }
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      description={description}
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className={QUIET}>Cancel</button>
          <button type="button" disabled={(!accepting && !basis.trim()) || saving} onClick={() => void submit()} className={PRIMARY}>
            {saving ? 'Recording…' : title}
          </button>
        </div>
      }
    >
      {(isDone || escalated) && <p className="mb-3 text-xs text-[var(--text-sec)]">“{response.basis}”</p>}
      {lines.length > 0 && accepting && (
        <ul className="mb-3 space-y-0.5 text-xs text-[var(--text-sec)]">{lines.map((l) => <li key={l}>{l}</li>)}</ul>
      )}
      <Field
        label={isDone && !accepting ? done.declineLabel : escalated && !accepting ? 'Why the boundary holds' : 'Basis'}
        required={!accepting}
        hint={accepting ? 'Optional. Their reason is recorded either way.' : 'Recorded with the decision this writes.'}
      >
        <TextArea rows={2} value={basis} onChange={(e) => setBasis(e.target.value)} />
      </Field>
      {err && <div className="text-xs text-[var(--coral)] mt-2">{err}</div>}
    </Modal>
  );
}

export function WithdrawResponseModal({ response, onClose, onDone }: { response: AssignmentResponseRow; onClose: () => void; onDone: () => void | Promise<void> }) {
  const [basis, setBasis] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  async function submit() {
    if (!response.response_id) return;
    setSaving(true); setErr(null);
    const res = await withdrawAssignmentResponse(response.response_id, basis.trim());
    setSaving(false);
    if (!res.ok) { setErr(res.error); return; }
    await onDone();
  }
  return (
    <Modal
      open
      onClose={onClose}
      title="Take this back"
      description="What you responded to stands as it was."
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className={QUIET}>Cancel</button>
          <button type="button" disabled={!basis.trim() || saving} onClick={() => void submit()} className={PRIMARY}>Take this back</button>
        </div>
      }
    >
      <Field label="Basis" required>
        <TextInput value={basis} onChange={(e) => setBasis(e.target.value)} placeholder="Why it no longer stands" />
      </Field>
      {err && <div className="text-xs text-[var(--coral)] mt-2">{err}</div>}
    </Modal>
  );
}
